import { useState } from 'react'
import Button from './Button'
import { Calendar } from './Calendar'
import { Popover, PopoverContent, PopoverTrigger } from './Popover'
import Label from './Label'
import { Clock, MapPin, CalendarIcon, ChevronDown } from 'lucide-react'
import { formatDisplayDate } from '@/utils/date'
import { formatSlotTime } from '@/utils/appointments'
import type { AvailableTimeSlotsDto } from '@/api/contracts/appointment-slots.contract'

export function SlotPickerSection({
  label = 'Select Date',
  selectedDate,
  onDateSelect,
  availableDates,
  slotsForDate,
  inPersonSlots,
  telemedicineSlots,
  isLoading,
  selectedSlot,
  onSlotSelect,
  nextAvailableDate,
}: {
  label?: string
  selectedDate?: Date
  onDateSelect: (d: Date | undefined) => void
  availableDates: string[]
  slotsForDate: AvailableTimeSlotsDto[]
  inPersonSlots: AvailableTimeSlotsDto[]
  telemedicineSlots: AvailableTimeSlotsDto[]
  isLoading: boolean
  selectedSlot?: AvailableTimeSlotsDto
  onSlotSelect: (s: AvailableTimeSlotsDto) => void
  nextAvailableDate?: string
}) {
  const [open, setOpen] = useState(false)
  const handleSelect = (d: Date | undefined) => { onDateSelect(d); setOpen(false) }
  const slotBtn = (slot: AvailableTimeSlotsDto) => (
    <Button key={slot.id} variant={selectedSlot?.id === slot.id ? 'default' : 'outline'} size='sm' onClick={() => onSlotSelect(slot)}>
      {formatSlotTime(slot)}
    </Button>
  )

  return (
    <>
      <div className='space-y-2'>
        <Label className='text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'>{label}</Label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant='outline' className='w-full justify-start text-left font-medium h-11'>
              <CalendarIcon className='mr-2 h-4 w-4' />
              {selectedDate ? formatDisplayDate(selectedDate) : 'Select a date'}
              <ChevronDown className='ml-auto h-4 w-4 opacity-50' />
            </Button>
          </PopoverTrigger>
          <PopoverContent className='w-auto p-0' align='start'>
            <Calendar selected={selectedDate} onSelect={handleSelect} availableDates={availableDates} defaultMonth={nextAvailableDate ? new Date(nextAvailableDate) : new Date()} />
          </PopoverContent>
        </Popover>
      </div>
      {selectedDate && (
        <div className='space-y-3'>
          <div className='flex items-center justify-between'>
            <Label className='text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'>Select Time</Label>
            <span className='text-xs font-medium text-muted-foreground'>{formatDisplayDate(selectedDate)}</span>
          </div>
          {isLoading ? (
            <div className='grid grid-cols-2 gap-2 sm:grid-cols-3'>{[1,2,3,4,5,6].map(i => <div key={i} className='h-9 bg-muted animate-pulse rounded-md' />)}</div>
          ) : slotsForDate.length > 0 ? (
            <div className='space-y-3'>
              {inPersonSlots.length > 0 && (
                <div>
                  <div className='text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5'><MapPin className='w-3.5 h-3.5' />In-Person</div>
                  <div className='grid grid-cols-2 gap-2 sm:grid-cols-3'>{inPersonSlots.map(slotBtn)}</div>
                </div>
              )}
              {telemedicineSlots.length > 0 && (
                <div>
                  <div className='text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5'><div className='w-3.5 h-3.5 rounded-full border-2 border-current' />Telemedicine</div>
                  <div className='grid grid-cols-2 gap-2 sm:grid-cols-3'>{telemedicineSlots.map(slotBtn)}</div>
                </div>
              )}
            </div>
          ) : (
            <div className='text-center py-6 text-muted-foreground bg-muted/30 rounded-xl border border-dashed border-border/70'>
              <Clock className='w-6 h-6 mx-auto mb-2 opacity-30' />
              <p className='text-sm font-medium'>No slots available</p>
              <p className='text-xs mt-1'>Try selecting another date</p>
            </div>
          )}
        </div>
      )}
    </>
  )
}
